import { AfterViewChecked, Component, DOCUMENT, ElementRef, inject, input, signal, ViewChild } from "@angular/core";
import { RewardCursor as RewardCursorEntity } from "@domain/User";

@Component({
  selector: "app-reward-cursor",
  template: `
    <img
      #cursor
      id="cursor"
      [class]="'cursor__image cursor--' + rewardCursor()"
      data-testid="reward-cursor"
      [src]="'assets/cursors/' + rewardCursor() + '.svg'"
      alt="This is a fake cursor"
    />
  `,
  styles: `
    #cursor {
      display: none;
      position: absolute;
      pointer-events: none;
      z-index: 9999;
    }
  `,
})
export class RewardCursor implements AfterViewChecked {
  @ViewChild("cursor", { read: ElementRef }) cursorElement?: ElementRef<HTMLImageElement>;

  private document: Document = inject(DOCUMENT);

  rewardCursor = input.required<RewardCursorEntity>();

  cursorElementWasPresent = signal(!!this.cursorElement);

  ngAfterViewChecked() {
    const cursorElementIsPresent = !!this.cursorElement;
    const cursorElementWasPresent = this.cursorElementWasPresent();

    const hasCursorElementAppeared = cursorElementIsPresent && !cursorElementWasPresent;
    const hasCursorElementDisappreared = !cursorElementIsPresent && cursorElementWasPresent;
    if (hasCursorElementAppeared) {
      this.addCursorMouseEventListener();
    } else if (hasCursorElementDisappreared) {
      this.removeCursorMouseEventListener();
    }

    this.cursorElementWasPresent.set(cursorElementIsPresent);
  }

  private addCursorMouseEventListener() {
    this.document.body.addEventListener("mouseover", this.handleMouseOverEvent.bind(this));
    this.document.body.addEventListener("mousemove", this.handleMouseMoveEvent.bind(this));
    this.document.body.addEventListener("mouseleave", this.handleMouseLeaveEvent.bind(this));
    this.document.body.addEventListener("mouseenter", this.handleMouseEnterEvent.bind(this));
  }

  private removeCursorMouseEventListener() {
    this.document.body.removeEventListener("mousemove", this.handleMouseMoveEvent.bind(this));
    this.document.body.removeEventListener("mouseleave", this.handleMouseLeaveEvent.bind(this));
    this.document.body.removeEventListener("mouseenter", this.handleMouseEnterEvent.bind(this));
  }

  private handleMouseMoveEvent(event: MouseEvent) {
    const cursorElement = this.cursorElement;
    if (!cursorElement) return;

    const { clientY, clientX } = event;

    cursorElement.nativeElement.style.top = `${clientY + 10}px`;
    cursorElement.nativeElement.style.left = `${clientX + 10}px`;
  }

  private handleMouseLeaveEvent() {
    const cursorElement = this.cursorElement;
    if (!cursorElement) return;

    if (cursorElement.nativeElement.style.display !== "none") {
      cursorElement.nativeElement.style.display = "none";
    }
  }

  private handleMouseEnterEvent(event: MouseEvent) {
    const cursorElement = this.cursorElement;
    if (!cursorElement) return;

    if (cursorElement.nativeElement.style.display !== "inline") {
      cursorElement.nativeElement.style.display = "inline";
    }

    const { clientY, clientX } = event;

    cursorElement.nativeElement.style.top = `${clientY + 10}px`;
    cursorElement.nativeElement.style.left = `${clientX + 10}px`;
  }

  private handleMouseOverEvent(event: MouseEvent) {
    this.handleMouseEnterEvent(event);

    this.document.removeEventListener("mouseover", this.handleMouseEnterEvent.bind(this));
  }
}
